import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { TrendingUp, TrendingDown, DollarSign, Users, ShoppingBag, Repeat, Award, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatNumber = (v) =>
  Number(v || 0).toLocaleString('pt-BR');

const PLAN_COLORS = {
  basic: 'bg-slate-500',
  premium: 'bg-gray-500',
  pro: 'bg-indigo-500',
  advanced: 'bg-purple-500',
  scale: 'bg-pink-500',
  business: 'bg-orange-500',
  elite: 'bg-amber-500',
  exclusive: 'bg-emerald-500',
};

const KpiCard = ({ title, value, change, changeLabel, icon: Icon, color = 'indigo', testId }) => {
  const positive = (change ?? 0) >= 0;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2" data-testid={`${testId}-value`}>{value}</p>
          {change !== undefined && change !== null && (
            <div className={`flex items-center mt-2 text-xs font-medium ${positive ? 'text-green-600' : 'text-red-600'}`}>
              {positive ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
              <span>{Math.abs(change).toFixed(1)}%</span>
              <span className="text-gray-500 ml-1">{changeLabel || 'vs período anterior'}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );
};

const RevenueChart = ({ series }) => {
  if (!series || series.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        Sem dados no período
      </div>
    );
  }

  const max = Math.max(...series.map(s => s.total), 1);
  const width = 760;
  const height = 220;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const step = innerW / Math.max(series.length - 1, 1);

  const points = series.map((d, i) => ({
    x: padding.left + i * step,
    y: padding.top + innerH - (d.total / max) * innerH,
    value: d.total,
    date: d.date,
    saas: d.saas,
    marketplace: d.marketplace,
  }));

  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  const areaPath = `${path} L ${points[points.length - 1].x} ${padding.top + innerH} L ${padding.left} ${padding.top + innerH} Z`;

  return (
    <div className="relative w-full overflow-x-auto" data-testid="revenue-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto min-w-[700px]">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + innerH * (1 - p)}
            x2={padding.left + innerW}
            y2={padding.top + innerH * (1 - p)}
            stroke="#e5e7eb"
            strokeDasharray="2 4"
          />
        ))}
        {/* Y-axis labels */}
        {[0, 0.5, 1].map((p, i) => (
          <text
            key={i}
            x={padding.left - 8}
            y={padding.top + innerH * (1 - p) + 4}
            textAnchor="end"
            fontSize="10"
            fill="#9ca3af"
          >
            {(max * p).toFixed(0)}
          </text>
        ))}
        {/* Area */}
        <defs>
          <linearGradient id="revGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#revGrad)" />
        <path d={path} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill="#4f46e5" />
            <title>{`${p.date}: ${formatBRL(p.value)} (SaaS ${formatBRL(p.saas)} + MKT ${formatBRL(p.marketplace)})`}</title>
          </g>
        ))}
        {/* X-axis labels (sample) */}
        {points.filter((_, i) => i % Math.max(1, Math.floor(points.length / 6)) === 0).map((p, i) => {
          const dt = new Date(p.date);
          const lbl = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`;
          return (
            <text
              key={i}
              x={p.x}
              y={height - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {lbl}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export const AdminMetrics = () => {
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [recent, setRecent] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [oRes, tRes, rRes] = await Promise.all([
          axios.get(`${API_URL}/admin/metrics/overview?days=${period}`, { withCredentials: true }),
          axios.get(`${API_URL}/admin/metrics/revenue-trend?days=${period}`, { withCredentials: true }),
          axios.get(`${API_URL}/admin/metrics/recent-payments?limit=10`, { withCredentials: true }),
        ]);
        setOverview(oRes.data);
        setTrend(tRes.data);
        setRecent(rRes.data.payments || []);
      } catch (err) {
        console.error('Error fetching metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [period]);

  if (loading || !overview) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-gray-200 rounded w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded-xl" />
              ))}
            </div>
            <div className="h-72 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const { saas, marketplace, sellers, plans_ranking } = overview;
  const topPlans = plans_ranking.filter(p => p.active_subscriptions > 0 || p.total_purchases > 0).slice(0, 5);
  const maxPurchases = Math.max(...plans_ranking.map(p => p.total_purchases), 1);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="metrics-title">
              Métricas de Pagamento
            </h1>
            <p className="text-base text-gray-600 mt-1">Receita, planos mais vendidos e conversão</p>
          </div>
          <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setPeriod(d)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  period === d ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`period-filter-${d}`}
              >
                {d} dias
              </button>
            ))}
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KpiCard
            title="Receita SaaS (período)"
            value={formatBRL(saas.revenue_period)}
            change={saas.revenue_change_pct}
            icon={DollarSign}
            color="indigo"
            testId="kpi-saas-revenue"
          />
          <KpiCard
            title="MRR (Receita Recorrente)"
            value={formatBRL(saas.mrr)}
            icon={Repeat}
            color="green"
            testId="kpi-mrr"
          />
          <KpiCard
            title="GMV Marketplace"
            value={formatBRL(marketplace.gmv_period)}
            icon={ShoppingBag}
            color="orange"
            testId="kpi-gmv"
          />
          <KpiCard
            title="Taxa de Conversão"
            value={`${sellers.conversion_rate.toFixed(1)}%`}
            icon={Activity}
            color="purple"
            testId="kpi-conversion"
          />
        </div>

        {/* Secondary stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="stat-total-sellers">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Vendedores</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(sellers.total)}</p>
            <p className="text-xs text-green-600 mt-1">+{sellers.new_in_period} novos</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="stat-paying-sellers">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Pagantes</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(sellers.paying)}</p>
            <p className="text-xs text-gray-500 mt-1">{formatNumber(sellers.active_subscriptions)} assinaturas ativas</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="stat-saas-avg">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Ticket Médio SaaS</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatBRL(saas.avg_ticket)}</p>
            <p className="text-xs text-gray-500 mt-1">{saas.paid_count_period} pagamentos</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="stat-marketplace-avg">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Ticket Médio MKT</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatBRL(marketplace.avg_ticket)}</p>
            <p className="text-xs text-gray-500 mt-1">{marketplace.orders_count_period} pedidos</p>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Tendência de Receita
              </h2>
              <p className="text-sm text-gray-500 mt-1">Receita diária (SaaS + Marketplace) - últimos {period} dias</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total no período</p>
              <p className="text-lg font-bold text-gray-900">{formatBRL(saas.revenue_period + marketplace.gmv_period)}</p>
            </div>
          </div>
          <RevenueChart series={trend?.series || []} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plans Ranking */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="plans-ranking">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Planos Mais Vendidos
            </h2>
            <div className="space-y-3">
              {topPlans.length === 0 && (
                <p className="text-sm text-gray-400 py-8 text-center">Sem dados de planos no momento</p>
              )}
              {topPlans.map((plan, idx) => {
                const barWidth = (plan.total_purchases / maxPurchases) * 100;
                const dotColor = PLAN_COLORS[plan.plan_key] || 'bg-gray-400';
                return (
                  <div key={plan.plan_key} data-testid={`plan-row-${plan.plan_key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
                        <span className="text-sm font-medium text-gray-700">
                          {idx + 1}. {plan.plan_name}
                        </span>
                        <span className="text-xs text-gray-400">{formatBRL(plan.price)}/mês</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-gray-900">{plan.active_subscriptions}</span>
                        <span className="text-xs text-gray-500 ml-1">ativas</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full ${dotColor} transition-all`}
                        style={{ width: `${Math.max(barWidth, 4)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{plan.total_purchases} pagamentos</span>
                      <span>{formatBRL(plan.total_revenue)} arrecadados</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Payments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="recent-payments">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              Pagamentos Recentes
            </h2>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {recent.length === 0 && (
                <p className="text-sm text-gray-400 py-8 text-center">Nenhum pagamento recente</p>
              )}
              {recent.map((p, i) => {
                const dt = p.created_at ? new Date(p.created_at) : null;
                const dateLabel = dt ? dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—';
                return (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${p.type === 'subscription' ? 'bg-indigo-100' : 'bg-orange-100'}`}>
                        {p.type === 'subscription' ? <Repeat className="w-4 h-4 text-indigo-600" /> : <ShoppingBag className="w-4 h-4 text-orange-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {p.type === 'subscription' ? `Assinatura ${p.plan || ''}` : 'Pedido'}
                        </p>
                        <p className="text-xs text-gray-500">{dateLabel}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{formatBRL(p.amount)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
